import { type MutableRefObject } from "react";

import { cn } from "~/shared/lib/utils";

import { calculateItemPosition } from "./message-scroll-nav-helpers";
import { useMessageScrollNav } from "./use-message-scroll-nav";

interface MessageScrollNavProps {
    messageIds: string[];
    scrollContainerRef: React.RefObject<HTMLDivElement | null>;
    // Когда задан — применяется вместо встроенного scrollIntoView.
    // Нужен parent'у, который умеет разворачивать свёрнутые
    // date-группы перед скроллом к скрытому сообщению.
    onScrollTo?: (messageId: string) => void;
    // Когда .current === true — юзер «приколочен к низу» (initial
    // reload или сразу после отправки нового сообщения). В этом
    // режиме активная точка прибивается к последнему сообщению, а
    // обычное правило «topmost из видимых» включается только когда
    // юзер сам уехал вверх (см. VirtualMessageList.exitStickyOnUserScroll).
    stickyBottomRef?: MutableRefObject<boolean>;
    // Детерминированный снимок collapsed-state из VirtualMessageList.
    // При collapse React unmount'ит [data-msg-anchor], при expand —
    // mount'ит новые. Используем как dep useEffect'а, чтобы
    // переподключать IntersectionObserver к актуальным DOM-узлам.
    collapsedKey?: string;
}

// Боковая навигация: фиксированная вертикальная линия с точками,
// по одной на каждое сообщение. Sits справа от ленты в родительском
// relative-блоке (см. VirtualMessageList) — НЕ скроллится вместе с
// контентом. Точки распределены равномерно по высоте линии (а не по
// реальному offsetTop bubble), чтобы при длинной ленте они не
// слипались с одного края. Активная точка — самое верхнее видимое
// сообщение в viewport (reading-position). Клик скроллит к нужному
// сообщению.
export function MessageScrollNav(props: MessageScrollNavProps) {
    const { messageIds } = props;
    const { activeId, handleClick } = useMessageScrollNav(props);

    if (messageIds.length <= 1) return null;

    return (
        <aside
            aria-label="Message navigation"
            // Absolute к relative-родителю VirtualMessageList — фиксируется
            // в viewport чата, не скроллится. Padding по вертикали отделяет
            // крайние точки от границ ленты.
            className="pointer-events-none absolute top-0 right-2 bottom-0 z-30 hidden w-6 py-6 md:block"
        >
            <div className="pointer-events-auto relative h-full w-full">
                <div className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-zinc-700/60" />
                {messageIds.map((id, idx) => {
                    const topPct = calculateItemPosition(idx, messageIds.length);
                    const isActive = id === activeId;
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => handleClick(id)}
                            aria-label={`Jump to message ${idx + 1}`}
                            aria-current={isActive ? "true" : undefined}
                            style={{ top: `${topPct}%` }}
                            className="group absolute left-1/2 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center bg-transparent focus:outline-none"
                        >
                            <span
                                className={cn(
                                    "block rounded-full transition-all duration-150 group-focus-visible:ring-2 group-focus-visible:ring-violet-400",
                                    isActive
                                        ? "h-2.5 w-2.5 bg-violet-400"
                                        : "h-1.5 w-1.5 bg-zinc-600 group-hover:bg-zinc-400",
                                )}
                            />
                        </button>
                    );
                })}
            </div>
        </aside>
    );
}
